// The Board: who is on a game's markets, from a `scanner` instance's status. One table per market of unit-size
// signatures (tag, counts, lifetime, re-quote, lift over baseline at each horizon, swell), click a signature to
// open its row of horizon drifts, and the market's live timeline underneath. Deterministic: it draws exactly
// what the Rust scanner computed; nothing is inferred here.

import { useCallback, useMemo, useState } from 'react';
import { useVisibilityAwarePoll } from '@/hooks/useVisibilityAwarePoll';
import { rustLane, fmt, type Instance, type Json } from '@/lib/rustLane/api';

interface HStat { ms: number; n: number; moved: number; rate: number | null; meanDriftCents: number | null; lift: number | null }
interface Sig {
  contracts: number; class: string; tag: string; informed: boolean; adds: number; pulls: number; takes: number; fills: number;
  firstWallMs: number; lastWallMs: number; lastSide: string | null; lastCents: number | null; lifetimeMs: number | null; requoteMs: number | null;
  pull: HStat[]; take: HStat[]; swellMs: { pull: number | null; take: number | null };
}
interface MarketScan {
  touch: { yesBidCents: number | null; noBidCents: number | null };
  horizonsMs: number[];
  baseline: { yes: Array<{ ms: number; n: number; downRate: number | null; upRate: number | null }>; no: Array<{ ms: number; n: number; downRate: number | null; upRate: number | null }> };
  signatures: Sig[];
  signaturesSeen: number;
  timeline: Array<{ wallMs: number; kind: string; side: string; cents: number; contracts: number; drift: Array<number | null> }>;
  notApplied: number;
}

const TAG_CLASS: Record<string, string> = {
  rec: 'bg-muted text-muted-foreground', retail: 'bg-sky-500/15 text-sky-400', sharp: 'bg-violet-500/15 text-violet-400', whale: 'bg-orange-500/15 text-orange-400',
};

function liftCell(h: HStat | undefined) {
  if (!h || h.n === 0) return <span className="text-muted-foreground">—</span>;
  const l = h.lift;
  const tone = l === null ? '' : l >= 3 ? 'text-emerald-400 font-semibold' : l >= 1.5 ? 'text-amber-400' : '';
  return <span className={tone} title={`${h.moved}/${h.n} moved · mean ${h.meanDriftCents?.toFixed(2) ?? '—'}¢`}>{l === null ? '—' : l >= 999 ? '∞' : `${l.toFixed(1)}×`}<span className="text-muted-foreground"> /{h.n}</span></span>;
}

export function Board({ instance, serverNowMs }: { instance: Instance; serverNowMs: number }) {
  const st = (instance.strategy ?? null) as Json | null;
  const markets = (st?.markets ?? {}) as Record<string, MarketScan>;
  const [open, setOpen] = useState<string | null>(null);
  const names = Object.keys(markets).sort();
  if (names.length === 0) return <p className="text-muted-foreground font-mono text-xs">scanner has no flow yet</p>;
  return (
    <div className="space-y-4 font-mono text-[11px]">
      {names.map((t) => {
        const m = markets[t]!;
        const hs = m.horizonsMs;
        const hi = Math.min(2, hs.length - 1);
        return (
          <div key={t} className="border-border rounded border p-2">
            <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
              <span className="font-semibold">{t.split('-').slice(-1)[0]} <span className="text-muted-foreground font-normal">{t}</span></span>
              <span className="text-muted-foreground">touch {fmt.cents((m.touch.yesBidCents ?? NaN) * 100)} yes / {fmt.cents((m.touch.noBidCents ?? NaN) * 100)} no · {m.signaturesSeen} sizes seen · baseline down@{hs[hi]! / 1000}s yes {((m.baseline.yes[hi]?.downRate ?? 0) * 100).toFixed(0)}% no {((m.baseline.no[hi]?.downRate ?? 0) * 100).toFixed(0)}%</span>
            </div>
            <table className="w-full">
              <thead><tr className="text-muted-foreground text-left"><th className="font-normal">size</th><th className="font-normal">tag</th><th className="text-right font-normal">adds</th><th className="text-right font-normal">pulls</th><th className="text-right font-normal">takes</th><th className="text-right font-normal">fills</th><th className="text-right font-normal">life</th><th className="text-right font-normal">requote</th>{hs.map((h) => <th key={h} className="text-right font-normal">pull@{h / 1000}s</th>)}<th className="text-right font-normal">take@{hs[hi]! / 1000}s</th><th className="text-right font-normal">swell</th><th className="text-right font-normal">last</th></tr></thead>
              <tbody>
                {m.signatures.map((s) => {
                  const key = `${t}|${s.contracts}`;
                  const isOpen = open === key;
                  return (
                    <SigRow key={key} s={s} hs={hs} hi={hi} isOpen={isOpen} onToggle={() => setOpen(isOpen ? null : key)} serverNowMs={serverNowMs} />
                  );
                })}
              </tbody>
            </table>
            <details className="mt-2">
              <summary className="text-muted-foreground cursor-pointer">timeline ({m.timeline.length})</summary>
              <table className="mt-1 w-full"><tbody>
                {m.timeline.slice(0, 40).map((r, i) => (
                  <tr key={i} className="border-border border-t"><td className="text-muted-foreground pr-2">{new Date(r.wallMs).toISOString().slice(11, 23)}</td><td className="pr-2">{r.kind}</td><td className="pr-2">{r.side}</td><td className="pr-2 text-right">{r.cents.toFixed(0)}¢</td><td className="pr-2 text-right">{r.contracts}</td><td className="text-muted-foreground">{r.drift.map((d, j) => d === null ? '·' : `${d > 0 ? '+' : ''}${d.toFixed(0)}@${hs[j]! / 1000}s`).join(' ')}</td></tr>
                ))}
              </tbody></table>
            </details>
          </div>
        );
      })}
    </div>
  );
}

function SigRow({ s, hs, hi, isOpen, onToggle, serverNowMs }: { s: Sig; hs: number[]; hi: number; isOpen: boolean; onToggle: () => void; serverNowMs: number }) {
  const age = serverNowMs - s.lastWallMs;
  return (
    <>
      <tr className={`hover:bg-accent/40 cursor-pointer ${s.informed ? 'bg-emerald-500/5' : ''}`} onClick={onToggle}>
        <td className="font-semibold">{s.contracts.toLocaleString()}</td>
        <td><span className={`rounded px-1.5 py-0.5 ${TAG_CLASS[s.class] ?? ''}`}>{s.tag}</span></td>
        <td className="text-right">{s.adds}</td><td className="text-right">{s.pulls}</td><td className="text-right">{s.takes}</td><td className="text-right">{s.fills}</td>
        <td className="text-right">{s.lifetimeMs === null ? '—' : fmt.ms(s.lifetimeMs)}</td>
        <td className="text-right">{s.requoteMs === null ? '—' : fmt.ms(s.requoteMs)}</td>
        {hs.map((h, j) => <td key={h} className="text-right">{liftCell(s.pull[j])}</td>)}
        <td className="text-right">{liftCell(s.take[hi])}</td>
        <td className="text-right">{s.swellMs.pull ? `${s.swellMs.pull / 1000}s` : '—'}</td>
        <td className="text-muted-foreground text-right">{age >= 0 ? fmt.age(age) : '—'} {s.lastSide ?? ''} {s.lastCents !== null ? `${s.lastCents.toFixed(0)}¢` : ''}</td>
      </tr>
      {isOpen && (
        <tr><td colSpan={11 + hs.length} className="bg-muted/40 p-2">
          <div className="grid gap-x-6 gap-y-1 md:grid-cols-2">
            <div><div className="text-muted-foreground mb-0.5">after its pulls (that side's bid drift, mean ¢ · moved down share · lift)</div>{s.pull.map((h) => <div key={h.ms}>@{h.ms / 1000}s: {h.n ? `${h.meanDriftCents?.toFixed(2)}¢ · ${((h.rate ?? 0) * 100).toFixed(0)}% · ${h.lift === null ? '—' : h.lift >= 999 ? '∞' : h.lift.toFixed(1) + '×'} (n=${h.n})` : 'no samples'}</div>)}</div>
            <div><div className="text-muted-foreground mb-0.5">after its takes (taker side's bid drift, moved up share, lift)</div>{s.take.map((h) => <div key={h.ms}>@{h.ms / 1000}s: {h.n ? `${h.meanDriftCents?.toFixed(2)}¢ · ${((h.rate ?? 0) * 100).toFixed(0)}% · ${h.lift === null ? '—' : h.lift >= 999 ? '∞' : h.lift.toFixed(1) + '×'} (n=${h.n})` : 'no samples'}</div>)}</div>
          </div>
          <div className="text-muted-foreground mt-1">first seen {new Date(s.firstWallMs).toISOString().slice(11, 19)}Z · class {s.class} · informed needs n≥5 and lift≥3 at {hs[hi]! / 1000}s</div>
        </td></tr>
      )}
    </>
  );
}

/** The Board for a game: finds (or tells you to deploy) the scanner instance on it and polls it. */
export function GameBoard({ gameKey, instances }: { gameKey: string; instances: Instance[] }) {
  const scanner = useMemo(() => instances.find((i) => i.kind === 'scanner' && (i.game ?? (i.target?.game as string)) === gameKey) ?? null, [instances, gameKey]);
  const [detail, setDetail] = useState<Instance | null>(null);
  const [now, setNow] = useState(0);
  const poll = useCallback(async (signal: AbortSignal) => {
    if (!scanner) return;
    try {
      const d = await rustLane.instance(scanner.id, signal);
      if (signal.aborted) return;
      setDetail(d);
      setNow(Date.now());
    } catch { /* the row's chips show the error on the next list poll */ }
  }, [scanner]);
  useVisibilityAwarePoll(poll, 1000, { enabled: scanner !== null });
  if (!scanner) return <p className="text-muted-foreground font-mono text-[11px]">No scanner on this game. Deploy kind <span className="font-semibold">scanner</span> (shadow) to it and the board fills in.</p>;
  if (!detail) return <p className="text-muted-foreground font-mono text-[11px]">loading…</p>;
  return <Board instance={detail} serverNowMs={now} />;
}
